import { Component } from "react";
import { Button } from "@/components/ui/button";

/**
 * Last-line-of-defense error boundary.
 * Catches any uncaught render or lifecycle error from the entire app and
 * shows a recoverable fallback so the user can keep working without a
 * blank/red screen of death.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Uncaught UI error:", error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-stone-50 p-6">
          <div
            data-testid="error-boundary-fallback"
            className="max-w-lg rounded-xl border border-stone-200 bg-white p-6 shadow-sm"
          >
            <h1 className="text-base font-semibold text-stone-900">
              Si è verificato un errore inatteso
            </h1>
            <p className="mt-2 text-sm text-stone-600">
              L'applicazione ha rilevato un problema ma è ancora attiva. Puoi
              riprovare l'azione o ricaricare la pagina.
            </p>
            <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-stone-100 p-2 text-[11px] text-stone-700">
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <div className="mt-4 flex gap-2">
              <Button onClick={this.reset} variant="outline" data-testid="error-boundary-retry">
                Riprova
              </Button>
              <Button onClick={this.reload} data-testid="error-boundary-reload">
                Ricarica pagina
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
